import moment from 'moment';
import type { BpReading, ClusterResult, GlucoseReading } from '../clustering/types';
import type { ClusterGraphSeries, GroupedVitalSignsBlock, VitalReadingRow } from '../types';

function byDate<T extends { recordedAt: string | null }>(members: T[]): T[] {
  return [...members]
    .filter((member) => member.recordedAt)
    .sort((a, b) => a.recordedAt!.localeCompare(b.recordedAt!));
}

export function buildBloodPressureClusterGraphs(
  result: ClusterResult<BpReading>
): ClusterGraphSeries[] {
  return result.clusters
    .map((cluster) => {
      const days = new Map<string, BpReading[]>();
      for (const reading of byDate(cluster.members)) {
        const date = reading.recordedAt!.slice(0, 10);
        days.set(date, [...(days.get(date) || []), reading]);
      }
      const labels = Array.from(days.keys()).sort();
      return {
        representativeMinutes: cluster.representativeMinutes,
        representativeTime: cluster.representativeTime,
        pointCount: labels.length,
        labels: labels.map((date) => moment(date).format('MMM D')),
        datasets: [
          {
            label: 'Systolic',
            data: labels.map((date) => {
              const readings = days.get(date)!;
              return readings.reduce((sum, reading) => sum + reading.systolic, 0) / readings.length;
            }),
          },
          {
            label: 'Diastolic',
            data: labels.map((date) => {
              const readings = days.get(date)!;
              return readings.reduce((sum, reading) => sum + reading.diastolic, 0) / readings.length;
            }),
          },
        ],
      };
    })
    .filter((graph) => graph.pointCount > 0)
    .sort((a, b) => a.representativeMinutes - b.representativeMinutes);
}

export function buildGlucoseClusterGraphs(
  result: ClusterResult<GlucoseReading>,
  count: number
): { visible: boolean; graphs: ClusterGraphSeries[] } {
  if (count <= 1) return { visible: false, graphs: [] };

  const graphs = result.clusters
    .map((cluster) => {
      const sorted = byDate(cluster.members);
      return {
        representativeMinutes: cluster.representativeMinutes,
        representativeTime: cluster.representativeTime,
        pointCount: sorted.length,
        labels: sorted.map((reading) => moment(reading.recordedAt!).format('MMM D')),
        datasets: [{ label: 'Glucose', data: sorted.map((reading) => reading.glucose) }],
      };
    })
    .filter((graph) => graph.pointCount > 0)
    .sort((a, b) => a.representativeMinutes - b.representativeMinutes);

  return { visible: graphs.length > 0, graphs };
}

function bpRow(reading: BpReading): VitalReadingRow {
  return {
    id: reading.id,
    recordedAt: reading.recordedAt,
    date: reading.recordedAt ? moment(reading.recordedAt).format('DD/MM/YYYY HH:mm') : 'Date unavailable',
    value: `${reading.systolic}/${reading.diastolic} mmHg`,
  };
}

function glucoseRow(reading: GlucoseReading): VitalReadingRow {
  return {
    id: reading.id,
    recordedAt: reading.recordedAt,
    date: reading.recordedAt ? moment(reading.recordedAt).format('DD/MM/YYYY HH:mm') : 'Date unavailable',
    value: `${reading.glucose} mg/dL`,
  };
}

function buildBloodPressureBlock(result: ClusterResult<BpReading>): GroupedVitalSignsBlock {
  return {
    vitalType: 'Blood Pressure',
    isEmpty: result.clusters.every((cluster) => !cluster.members.length) && !result.unscheduled.length,
    groups: [
      ...result.clusters.map((cluster) => ({
        representativeMinutes: cluster.representativeMinutes,
        representativeTime: cluster.representativeTime,
        unscheduled: false,
        readings: byDate(cluster.members).map(bpRow),
      })),
      ...(result.unscheduled.length ? [{
        representativeMinutes: null,
        representativeTime: 'Unscheduled',
        unscheduled: true,
        readings: [
          ...byDate(result.unscheduled),
          ...result.unscheduled.filter((reading) => !reading.recordedAt),
        ].map(bpRow),
      }] : []),
    ],
  };
}

function buildGlucoseBlock(result: ClusterResult<GlucoseReading>): GroupedVitalSignsBlock {
  return {
    vitalType: 'Glucose',
    isEmpty: result.clusters.every((cluster) => !cluster.members.length) && !result.unscheduled.length,
    groups: [
      ...result.clusters.map((cluster) => ({
        representativeMinutes: cluster.representativeMinutes,
        representativeTime: cluster.representativeTime,
        unscheduled: false,
        readings: byDate(cluster.members).map(glucoseRow),
      })),
      ...(result.unscheduled.length ? [{
        representativeMinutes: null,
        representativeTime: 'Unscheduled',
        unscheduled: true,
        readings: [
          ...byDate(result.unscheduled),
          ...result.unscheduled.filter((reading) => !reading.recordedAt),
        ].map(glucoseRow),
      }] : []),
    ],
  };
}

export function buildGroupedVitalSignsRecord(
  bloodPressure: ClusterResult<BpReading>,
  glucose: ClusterResult<GlucoseReading>
): GroupedVitalSignsBlock[] {
  return [buildBloodPressureBlock(bloodPressure), buildGlucoseBlock(glucose)];
}

